import {CouponInfoSheet} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/coupon.types';
import {Product} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/product.types';
import {GetWalletV2Response} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/coupons/v1/wallet/service.types';
import {
  Flyout,
  GenericIconName,
  ModalDialog,
  Z_INDEXES,
} from '@aboutyou/ui-components';
import React, {FC, Fragment, useMemo, useRef, useState} from 'react';
import styled from 'styled-components';
import {CouponInfoContentPopper} from '../../../CouponInfoContentPopper/CouponInfoContentPopper';
import {CTA} from '../../CTA';
import {BodyHeaderSection} from '../../Components/BodyHeaderSection';
import {HighTierCouponsSection} from '../../Components/HighTierCouponsSection';
import {MoreCouponsSection} from '../../Components/MoreCouponsSection';

export const COUPON_WALLET_INFO_ICON_CUSTOM_KEY =
  'COUPON_WALLET_INFO_ICON_CUSTOM_KEY';

export interface WalletBodyContentProps {
  setSelectedProduct?: (product: Product | undefined) => void;
}

/**
 *
 * @param props
 * @returns
 * @deprecated shall be removed once coins is live
 */
export const WalletBodyContentView: FC<
  WalletBodyContentProps & GetWalletV2Response
> = ({sections, setSelectedProduct}) => {
  const {hasEmptySection, hasNoCoupons, hasPausedSection} = useMemo(
    () => getIsEmptyOrPausedEmptySection(sections),
    [sections],
  );

  const [infoIconContent, setInfoIconContent] = useState<CouponInfoSheet>();
  const onCloseInfoSheet = useRef<VoidFunction>(undefined);

  const handleInfoIconClick = (couponInfo?: CouponInfoSheet) => {
    setInfoIconContent(couponInfo);
  };

  return (
    <>
      <Wrapper
        data-paused-section={hasPausedSection}
        data-empty-section={hasEmptySection}
        data-no-coupons={hasNoCoupons}
        data-testid="couponWalletBodyContent"
      >
        {sections.map((section, index) => {
          switch (section.type?.$case) {
            case 'couponsHeaderSection':
              return (
                <BodyHeaderSection
                  key={`${section.type.couponsHeaderSection.headline}-${index}`}
                  headline={section.type.couponsHeaderSection.headline}
                  subline={section.type.couponsHeaderSection.subline}
                  iconName={GenericIconName.Coupon}
                />
              );
            case 'emptyCouponSection':
              return (
                <Fragment
                  key={`${section.type.emptyCouponSection.headline}-${index}`}
                >
                  <BodyHeaderSection
                    headline={section.type.emptyCouponSection.headline}
                    subline={section.type.emptyCouponSection.subline}
                    iconName={GenericIconName.Coupon}
                    reduceBottomSpace
                    data-testid="emptySection"
                  />
                  <CTA
                    url={section.type.emptyCouponSection.cta.linkTarget.url}
                    label={section.type.emptyCouponSection.cta.label}
                    isHighTierCTA
                    addArrowIcon
                  />
                </Fragment>
              );
            case 'couponsPausedSection':
              return (
                <Fragment
                  key={`${section.type.couponsPausedSection.headline}-${index}`}
                >
                  <BodyHeaderSection
                    headline={section.type.couponsPausedSection.headline}
                    subline={section.type.couponsPausedSection.subline}
                    iconName={GenericIconName.PauseRound}
                    reduceBottomSpace
                    data-testid="pausedSection"
                  />
                  <CTA
                    url={section.type.couponsPausedSection.cta.linkTarget.url}
                    label={section.type.couponsPausedSection.cta.label}
                    isHighTierCTA
                    addArrowIcon
                  />
                </Fragment>
              );
            case 'highTierCouponsSection':
              return (
                <HighTierCouponsSection
                  key={`${section.type.highTierCouponsSection.headline}-${index}`}
                  headline={section.type.highTierCouponsSection.headline}
                  coupons={section.type.highTierCouponsSection.coupons}
                  handleInfoIconClick={handleInfoIconClick}
                  stateManagerCustomKey={COUPON_WALLET_INFO_ICON_CUSTOM_KEY}
                  onCloseInfoSheet={onCloseInfoSheet}
                  setSelectedProduct={setSelectedProduct}
                  makeInert={hasPausedSection}
                />
              );
            case 'moreCouponsSection':
              return (
                <StyledMoreCouponsSection
                  key={`${section.type.moreCouponsSection.headline}-${index}`}
                  headline={section.type.moreCouponsSection.headline}
                  coupons={section.type.moreCouponsSection.coupons}
                  handleInfoIconClick={handleInfoIconClick}
                  stateManagerCustomKey={COUPON_WALLET_INFO_ICON_CUSTOM_KEY}
                  setSelectedProduct={setSelectedProduct}
                  makeInert={hasPausedSection}
                />
              );
            default:
              return null;
          }
        })}
      </Wrapper>
      <StyledCouponInfoContentPopper
        LayoutComponent={process.env.STACK === 'mobile' ? Flyout : ModalDialog}
        data-testid="couponCard"
        couponInfo={infoIconContent}
        stateManagerCustomKey={COUPON_WALLET_INFO_ICON_CUSTOM_KEY}
        onClose={() => {
          onCloseInfoSheet.current?.();
        }}
      />
    </>
  );
};

const Wrapper = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;

  &[data-empty-section='true'],
  &[data-paused-section='true'][data-no-coupons='true'] {
    justify-content: center;
  }
`;

const getIsEmptyOrPausedEmptySection = (
  sections: GetWalletV2Response['sections'],
) => {
  let hasEmptySection = false;
  let hasPausedSection = false;
  let hasHighTierCoupons = false;
  let hasLowTierCoupons = false;

  sections.forEach(section => {
    if (section?.type?.$case === 'emptyCouponSection') {
      hasEmptySection = true;
    } else if (section?.type?.$case === 'couponsPausedSection') {
      hasPausedSection = true;
    } else if (section?.type?.$case === 'highTierCouponsSection') {
      hasHighTierCoupons =
        section.type.highTierCouponsSection.coupons.length > 0;
    } else if (section?.type?.$case === 'moreCouponsSection') {
      hasLowTierCoupons = section.type.moreCouponsSection.coupons.length > 0;
    }
  });

  return {
    hasPausedSection,
    hasEmptySection,
    hasNoCoupons: !hasHighTierCoupons && !hasLowTierCoupons,
  };
};

const StyledCouponInfoContentPopper = styled(CouponInfoContentPopper)`
  &&& {
    z-index: ${Z_INDEXES.FLYOUT_FADE_ON_DRAWER};
  }
`;

const StyledMoreCouponsSection = styled(MoreCouponsSection)`
  padding: 24px 16px;
`;
